/**
 * Genie backend service — wraps /run and /run/stream endpoints.
 * Base URL is read from CTY_GENIE_BASE_URL env var.
 */

const GENIE_BASE_URL =
  (import.meta.env.CTY_GENIE_BASE_URL as string | undefined) ??
  'https://dev.genie.circuitry.ai';

// ---------------------------------------------------------------------------
// Request / Response types (mirrors OpenAPI RunRequest / RunResponse)
// ---------------------------------------------------------------------------

export interface GenieRunRequest {
  message: string;
  conversation_id?: string;
  /** Bearer token for vision tools */
  token?: string;
  image_source_id?: string;
  image_b64?: string;
  image_media_type?: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
  host_functions?: Array<{
    name: string;
    signature: string;
    description: string;
    when_to_use: string;
  }>;
}

export interface GenieTraceEntry {
  step: number;
  type: string;
  label?: string;
  content?: string;
  tool?: string;
  inputs?: Record<string, unknown>;
  output?: unknown;
  duration_ms?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
}

export interface GenieRunResponse {
  conversation_id: string;
  type: string | null;
  tool_used: string | null;
  output: unknown;
  message: string | null;
  trace: GenieTraceEntry[] | null;
  tokens: { prompt: number; completion: number; total: number } | null;
  provided_schema_for_template: string | null;
}

// ---------------------------------------------------------------------------
// /run  — single round-trip
// ---------------------------------------------------------------------------

export async function runGenie(payload: GenieRunRequest): Promise<GenieRunResponse> {
  const response = await fetch(`${GENIE_BASE_URL}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`Genie /run failed: ${response.status} ${response.statusText}`);
  }
  return response.json() as Promise<GenieRunResponse>;
}

// ---------------------------------------------------------------------------
// /run/stream  — SSE streaming
// ---------------------------------------------------------------------------

export interface GenieStreamCallbacks {
  /** Called for each trace entry as it arrives */
  onTraceEntry?: (entry: GenieTraceEntry) => void;
  /** Called once with the complete final response */
  onFinalResponse: (response: GenieRunResponse) => void;
  /** Called on network / parse error */
  onError: (error: Error) => void;
}

export async function streamGenie(
  payload: GenieRunRequest,
  callbacks: GenieStreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${GENIE_BASE_URL}/run/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (err) {
    callbacks.onError(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  if (!response.ok) {
    callbacks.onError(
      new Error(`Genie /run/stream failed: ${response.status} ${response.statusText}`)
    );
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError(new Error('Response body is not readable'));
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let gotFinalResponse = false;
  // Local servers emit the result as a tool_result trace step rather than a
  // separate {"type":"message"} envelope. Capture the last one so we can
  // synthesise a GenieRunResponse if no message event arrives.
  let lastToolResultStep: GenieTraceEntry | null = null;
  let streamedConversationId = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE spec: events are separated by blank lines (\n\n)
      // Process complete lines; keep any trailing partial line in buffer.
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        // Skip SSE comments and keep-alive empty lines
        if (!trimmed || trimmed.startsWith(':')) continue;

        // Accept both "data: {json}" (SSE) and raw "{json}" lines (local dev)
        const raw = trimmed.startsWith('data: ')
          ? trimmed.slice(6).trim()
          : trimmed;

        if (raw === '[DONE]') {
          gotFinalResponse = true;
          break;
        }

        let parsed: Record<string, unknown>;
        try {
          parsed = JSON.parse(raw) as Record<string, unknown>;
        } catch {
          // Malformed JSON line — skip
          continue;
        }

        // Capture conversation_id wherever it appears in the stream
        if (typeof parsed.conversation_id === 'string' && parsed.conversation_id) {
          streamedConversationId = parsed.conversation_id;
        }

        // SSE envelope formats:
        //   { "type": "trace",   "step": { step, type, content, ... } }   ← trace entry
        //   { "type": "message", "conversation_id": "...", "output": ... } ← final (hosted)
        //   { "type": "trace", "step": { "type": "tool_result", "output": ... } } ← final (local)
        if (parsed.type === 'trace' && parsed.step) {
          const step = parsed.step as unknown as GenieTraceEntry;
          callbacks.onTraceEntry?.(step);
          // Track the latest tool_result step — used as fallback if no envelope arrives
          if (step.type === 'tool_result' && step.output != null) {
            lastToolResultStep = step;
          }
        } else if (parsed.type === 'message' || parsed.type === 'tool_result') {
          // Hosted servers send {"type":"message",...}
          // Local dev servers send {"type":"tool_result","conversation_id":...}
          gotFinalResponse = true;
          callbacks.onFinalResponse(parsed as unknown as GenieRunResponse);
          return;
        }
      }

      if (gotFinalResponse) break;
    }
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') {
      callbacks.onError(err instanceof Error ? err : new Error(String(err)));
    }
    return;
  } finally {
    reader.releaseLock();
  }

  if (gotFinalResponse) return;

  // Stream ended without a {"type":"message"} envelope (local dev servers).
  // Reconstruct a GenieRunResponse from the last tool_result trace step.
  if (lastToolResultStep?.output != null) {
    callbacks.onFinalResponse({
      conversation_id: streamedConversationId,
      type: 'tool_result',
      tool_used: lastToolResultStep.tool ?? null,
      output: lastToolResultStep.output,
      message: null,
      trace: null,
      tokens: null,
      provided_schema_for_template: null,
    } as GenieRunResponse);
  } else {
    callbacks.onError(new Error('Stream ended without a final response from Genie.'));
  }
}
